<template>
    <component
        :is="IconComponent"
        class="c-appIcon"
        :class="{
            [`c-appIcon--${size}`]: size,
            [`c-appIcon--fixedWidth`]: fixedWidth,
            [`c-appIcon--spin`]: spin,
        }"
        :data-qa="iconFileName"
        alt=""
    />
</template>

<script setup>
import {
    computed, defineAsyncComponent, markRaw, ref, watch,
} from 'vue';
import * as logger from '../../../utils/logger';

const props = defineProps({
    name: {
        type: String,
        required: true,
    },
    size: {
        type: String,
    },
    fixedWidth: {
        type: Boolean,
        default: false,
    },
    spin: {
        type: Boolean,
        default: false,
    },
});

const map = {
    '3xl': 'm',
    '2xl': 'm',
    xl: 's',
    l: 's',
    m: 's',
    s: 's',
    xs: 's',
    '2xs': 's',
};

const version = computed(() => map[props.size ?? 'm']);

const iconFileName = computed(() => `${props.name}--${version.value}.svg`);

const IconComponent = ref(null);

/* Get paths of all icons, transform them into async components */
const allIconPaths = import.meta.glob('@icons/*.svg');
const iconKeys = Object.keys(allIconPaths);
const allIcons = iconKeys.reduce((acc, cur) => ({
    ...acc,
    [cur]: markRaw(defineAsyncComponent(allIconPaths[cur])),
}), {});

const loadIcon = async (fileName) => {
    try {
        const componentKey = iconKeys.find(iconKey => iconKey.split('/').at(-1) === fileName);
        IconComponent.value = allIcons[componentKey];
    } catch (exception) {
        logger.info({
            exception,
            fingerprint: ['icon chunk loading failed'],
        });
    }
};

watch(iconFileName, loadIcon, { immediate: true });
</script>

<style lang="scss">
@import '~@karriereat/global-styles/scss/settings/font-size';

.c-appIcon {
    position: relative;
    top: -0.05em;
    display: inline-block;
    width: auto;
    height: 1em;
    fill: currentColor;

    &--xs {
        // stylelint-disable-next-line property-disallowed-list
        font-size: $k-font-size--xs;
    }

    &--s {
        // stylelint-disable-next-line property-disallowed-list
        font-size: $k-font-size--s;
    }

    &--l {
        // stylelint-disable-next-line property-disallowed-list
        font-size: $k-font-size--l;
    }

    &--xl {
        // stylelint-disable-next-line property-disallowed-list
        font-size: $k-font-size--xl;
    }

    &--2xl {
        // stylelint-disable-next-line property-disallowed-list
        font-size: $k-font-size--2xl;
    }

    // There is no `3xl` font size, but we need an `3xl` icon size.
    &--3xl {
        // stylelint-disable-next-line property-disallowed-list
        font-size: 2.75em;
    }

    &--fixedWidth {
        width: 1em;
        text-align: center;
    }

    &--spin {
        animation: spin 1s infinite steps(12);
    }

    &--fill {
        fill: currentColor;
        stroke: none;
    }

    @keyframes spin {
        100% {
            transform: rotate(360deg);
        }
    }
}
</style>
